import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Cómo llega una respuesta de la IA, igual en todas partes (el asistente del
 * editor, la mascota flotante del panel y el chat de Inicio):
 *
 *   1. Pensando: la mascota gira y «Pensando…» brilla. Un momento mínimo
 *      aunque la respuesta ya esté —una que aparece de golpe se lee como un
 *      salto de pantalla, no como alguien contestando—.
 *   2. Escribiendo: el texto entra palabra a palabra, cada una con un fundido
 *      corto. Contra el reloj y con un tope de duración: una respuesta larga no
 *      se hace eterna.
 *   3. Hecho: el texto queda quieto y entra lo que va debajo (botones,
 *      tarjetas).
 *
 * Con «reducir movimiento» se salta todo y la respuesta sale entera.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
/** Lo mínimo que se ve «pensando», aunque la respuesta ya esté. */
export const AI_THINK_MS = 550;
function prefersReducedMotion() {
    return typeof window !== 'undefined' && Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);
}
/**
 * La fase de una respuesta. `animate` falso —una respuesta vieja, que ya se
 * leyó— la deja hecha desde el principio. Devuelve también con qué avisar de
 * que el texto terminó de escribirse.
 */
export function useAiReplyPhase(animate, thinkMs = AI_THINK_MS) {
    const [phase, setPhase] = useState(() => (animate && !prefersReducedMotion() ? (thinkMs > 0 ? 'thinking' : 'typing') : 'done'));
    useEffect(() => {
        if (phase !== 'thinking')
            return;
        const timer = window.setTimeout(() => setPhase('typing'), thinkMs);
        return () => window.clearTimeout(timer);
    }, [phase, thinkMs]);
    const finish = useCallback(() => setPhase('done'), []);
    return [phase, finish];
}
/** «Pensando…» con un brillo que lo recorre. */
export function AiThinkingLabel({ label }) {
    return _jsx("span", { className: "cwa-ai-thinking", children: label });
}
/**
 * El texto escribiéndose palabra a palabra. Con `active` falso se pinta entero
 * y quieto.
 */
export function AiTypingText({ text, active, onDone, wordsPerSecond = 28, maxMs = 1800 }) {
    const tokens = useRef([]);
    tokens.current = text.split(/(\s+)/).filter(Boolean);
    const [shown, setShown] = useState(0);
    const doneRef = useRef(onDone);
    doneRef.current = onDone;
    useEffect(() => {
        if (!active)
            return;
        const total = tokens.current.length;
        /* Palabras por segundo, o más si así no cabe en el tope. */
        const rate = Math.max(wordsPerSecond, (total / maxMs) * 1000);
        const start = performance.now();
        let frame = 0;
        const step = (now) => {
            const count = Math.min(total, Math.ceil(((now - start) / 1000) * rate));
            setShown(count);
            if (count < total)
                frame = requestAnimationFrame(step);
            else
                doneRef.current?.();
        };
        setShown(0);
        frame = requestAnimationFrame(step);
        return () => cancelAnimationFrame(frame);
    }, [active, text, wordsPerSecond, maxMs]);
    if (!active)
        return _jsx(_Fragment, { children: text });
    return (_jsxs(_Fragment, { children: [_jsx("span", { className: "cwa-sr-only", children: text }), _jsx("span", { "aria-hidden": "true", children: tokens.current.slice(0, shown).map((token, index) => (/\s/.test(token) ? token : _jsx("span", { className: "cwa-ai-type-word", children: token }, index))) })] }));
}
//# sourceMappingURL=AiReply.js.map